import { useEffect, useState } from 'react';
import { PHASES_OF_PLAY } from '@4cm/core/phaseOfPlay';
import type { PhaseCoverage, PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import type { Corner } from '../../components/primitives';
import { supabase } from '../supabase';
import { loadLocalSessions } from '../localTeam';

export interface WeekDay {
  date: Date;
  d: string;
  label: string;
  phase: PhaseOfPlay | null;
  phaseCoverage: Partial<PhaseCoverage>;
  corner: Corner | null;
  today: boolean;
}

interface SessionCoverageRow {
  date: string;
  phase_coverage: Record<string, number> | null;
  corner_coverage: Record<string, number> | null;
}

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function startOfWeek(d: Date) {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const day = out.getDay() || 7;
  out.setDate(out.getDate() - (day - 1));
  return out;
}

function pickCorner(coverage: Record<string, number> | null | undefined): Corner | null {
  if (!coverage) return null;
  const entries = Object.entries(coverage);
  if (!entries.length) return null;
  entries.sort((a, b) => Number(b[1]) - Number(a[1]));
  const [topKey] = entries[0];
  if (topKey.startsWith('tech')) return 'tech';
  if (topKey.startsWith('phys')) return 'phys';
  if (topKey.startsWith('psych')) return 'psych';
  if (topKey.startsWith('social')) return 'social';
  return null;
}

function toPhaseCoverage(coverage: Record<string, number> | null | undefined): Partial<PhaseCoverage> {
  if (!coverage) return {};
  const out: Partial<PhaseCoverage> = {};
  for (const phase of PHASES_OF_PLAY) {
    const value = coverage[phase];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      out[phase] = value;
    }
  }
  return out;
}

function pickPhase(coverage: Partial<PhaseCoverage>): PhaseOfPlay | null {
  let best: PhaseOfPlay | null = null;
  let bestValue = 0;
  for (const phase of PHASES_OF_PLAY) {
    const value = coverage[phase] ?? 0;
    if (value > bestValue) {
      best = phase;
      bestValue = value;
    }
  }
  return best;
}

export function useWeekStrip(teamId: string | undefined) {
  const [days, setDays] = useState<WeekDay[]>([]);

  useEffect(() => {
    if (!teamId) {
      setDays([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const start = startOfWeek(new Date());
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      const todayStr = new Date().toDateString();

      let rows: SessionCoverageRow[] = [];
      try {
        const { data } = await supabase
          .from('sessions')
          .select('date, phase_coverage, corner_coverage')
          .eq('team_id', teamId)
          .gte('date', start.toISOString())
          .lt('date', end.toISOString());
        if (cancelled) return;
        rows = (data ?? []) as SessionCoverageRow[];
      } catch {
        if (cancelled) return;
      }
      // Fallback: localStorage sessions
      if (rows.length === 0) {
        rows = loadLocalSessions()
          .filter((s) => s.team_id === teamId)
          .filter((s) => {
            const d = new Date(s.date).getTime();
            return d >= start.getTime() && d < end.getTime();
          })
          .map((s) => ({ date: s.date, phase_coverage: s.phase_coverage, corner_coverage: null }));
      }
      const out: WeekDay[] = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        const dStr = date.toDateString();
        const todays = rows.filter((s) => new Date(s.date).toDateString() === dStr);
        const phaseCoverage = todays.reduce<Partial<PhaseCoverage>>((acc, session) => {
          const coverage = toPhaseCoverage(session.phase_coverage);
          for (const phase of PHASES_OF_PLAY) {
            acc[phase] = (acc[phase] ?? 0) + (coverage[phase] ?? 0);
          }
          return acc;
        }, {});
        const phase = pickPhase(phaseCoverage);
        const corner = !phase && todays.length
          ? pickCorner(todays[0].corner_coverage)
          : null;
        out.push({
          date,
          d: WEEKDAY_LABELS[i],
          label: phase ?? (corner ? `4C-${6 - i}` : 'rest'),
          phase,
          phaseCoverage,
          corner,
          today: dStr === todayStr,
        });
      }
      setDays(out);
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  return days;
}
